import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { CartContext } from "../../context/CartContext";
import axios from "axios";
import "./OrderSuccess.css";

const OrderSuccess = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { clearCartSilently, clearCart } = useContext(CartContext);
  
  const hasClearedRef = useRef(false);
  const [order, setOrder] = useState(location.state?.order || null);
  const [loading, setLoading] = useState(!location.state?.order);

  const shortOrderId = id ? id.slice(-6).toUpperCase() : "";

  useEffect(() => {
    const paymentSuccess =
      location.state?.paymentSuccess ||
      sessionStorage.getItem("lastOrderSuccess") === id;

    if (!id || !paymentSuccess) {
      navigate("/", { replace: true });
      return;
    }

    // 1. Clear Cart exactly once
    if (!hasClearedRef.current) {
      hasClearedRef.current = true;
      if (typeof clearCartSilently === "function") {
        clearCartSilently();
      } else if (typeof clearCart === "function") {
        clearCart();
      }
      sessionStorage.removeItem("lastOrderSuccess");
    }

    // 2. Fetch order details if not passed via location state
    if (!order) {
      const fetchOrderDetails = async () => {
        try {
          const token = localStorage.getItem("token");
          const res = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL || "http://localhost:5000"}/api/orders/${id}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setOrder(res.data.order || res.data);
        } catch (error) {
          console.error("Failed to fetch order details", error);
        } finally {
          setLoading(false);
        }
      };
      fetchOrderDetails();
    }
  }, [id, location.state, navigate, clearCartSilently, clearCart, order]);

  // Fallback UI if loading or fetch fails
  if (loading) {
    return (
      <div className="order-success-page">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="order-success-page">
      <div className="order-success-container">
        
        {/* HEADER SECTION */}
        <div className="success-header">
          <div className="success-icon-wrapper">
            <i className="fa-solid fa-check"></i>
          </div>
          <h1>Thank you for your order!</h1>
          <p>Your payment was successful and your order is being processed.</p>
          <div className="order-badge">Order #{shortOrderId || "-"}</div>
        </div>

        {/* ORDER DETAILS GRID */}
        {order && (
          <div className="order-details-grid">
            
            {/* LEFT COLUMN: ITEMS */}
            <div className="order-items-section">
              <h2>Order Items</h2>
              <div className="items-list">
                {order.items?.map((item, index) => (
                  <div className="order-item-card" key={index}>
                    <img 
                      src={item.image || item.product?.image} 
                      alt={item.name} 
                      className="item-img"
                    />
                    <div className="item-info">
                      <h4>{item.name}</h4>
                      {item.sku && <p className="item-sku">SKU: {item.sku}</p>}
                    </div>
                    <div className="item-price-qty">
                      <p className="item-price">₹{item.price}</p>
                      <p className="item-qty">Qty: {item.quantity}</p>
                    </div>
                    <div className="item-total">
                      <p>₹{Number(item.price) * Number(item.quantity)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT COLUMN: SUMMARY & ADDRESS */}
            <div className="order-summary-section">
              
              <div className="summary-card">
                <h2>Summary</h2>
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>₹{order.subtotal || 0}</span>
                </div>
                {order.discount > 0 && (
                  <div className="summary-row discount">
                    <span>Discount</span>
                    <span>- ₹{order.discount}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>{order.shippingFee === 0 ? "Free" : `₹${order.shippingFee}`}</span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-row total">
                  <span>Total</span>
                  <span>₹{order.totalAmount || 0}</span>
                </div>
              </div>

              {order.shippingAddress && (
                <div className="address-card">
                  <h2>Shipping Details</h2>
                  <p className="address-name">{order.shippingAddress.fullName}</p>
                  <p>{order.shippingAddress.address}</p>
                  {order.shippingAddress.locality && <p>{order.shippingAddress.locality}</p>}
                  <p>{order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.pincode}</p>
                  <p className="address-phone"><i className="fa-solid fa-phone"></i> +91 {order.shippingAddress.phone}</p>
                </div>
              )}

            </div>
          </div>
        )}

        {/* ACTION BUTTONS */}
        <div className="success-actions">
          <Link to="/shop" className="btn-continue">
            Continue Shopping
          </Link>
          <Link to={`/trackorder`} className="btn-track">
            Track Order
          </Link>
        </div>

      </div>
    </div>
  );
};

export default OrderSuccess;